import React, { ReactNode } from "react";

import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip, { TooltipProps } from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Button, ButtonProps } from "@mapmycustomers/ui";

import IWizardStep from "@app/component/wizard/types/IWizardStep";

import styles from "./StandardStep.module.scss";

interface Props extends Pick<IWizardStep<any>, "onCancel" | "onGoBack" | "onGoForward"> {
  backButtonDisabled?: boolean;
  backButtonHidden?: boolean;
  backButtonProps?: Omit<ButtonProps, "children" | "disabled" | "hidden" | "onClick">;
  backButtonText?: ReactNode;
  canClose?: boolean;
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  footer?: ReactNode;
  nextButtonDisabled?: boolean;
  nextButtonHidden?: boolean;
  nextButtonProps?: Omit<ButtonProps, "children" | "disabled" | "hidden" | "onClick">;
  nextButtonText?: ReactNode;
  nextButtonTooltipProps?: TooltipProps;
  showHeader?: boolean;
  title?: ReactNode;
}

const StandardStep: React.FC<Props> = ({
  backButtonDisabled,
  backButtonHidden,
  backButtonProps,
  backButtonText,
  canClose = true,
  children,
  className,
  containerClassName,
  footer,
  nextButtonDisabled,
  nextButtonHidden,
  nextButtonProps,
  nextButtonText,
  nextButtonTooltipProps,
  onCancel,
  onGoBack,
  onGoForward,
  showHeader = true,
  title,
}) => {
  const intl = useIntl();

  return (
    <div className={cn(styles.container, containerClassName)}>
      {showHeader && (
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          {canClose && (
            <div className={styles.closeIconWrapper}>
              <FontAwesomeIcon className={styles.closeIcon} icon={faXmark} onClick={onCancel} />
            </div>
          )}
        </div>
      )}
      <div className={cn(styles.content, className)}>{children}</div>
      {footer === undefined ? (
        <div className={styles.footer}>
          <div>
            <Button
              disabled={backButtonDisabled}
              hidden={backButtonHidden}
              onClick={onGoBack}
              {...backButtonProps}
            >
              {backButtonText ||
                intl.formatMessage({
                  id: "wizard.standardStep.footer.back",
                  defaultMessage: "Go Back",
                  description: "Back button title in a StandardStep of a Wizard component",
                })}
            </Button>
          </div>
          <div>
            <Tooltip {...nextButtonTooltipProps}>
              <Button
                disabled={nextButtonDisabled}
                hidden={nextButtonHidden}
                onClick={onGoForward}
                type="primary"
                {...nextButtonProps}
              >
                {nextButtonText ||
                  intl.formatMessage({
                    id: "wizard.standardStep.footer.next",
                    defaultMessage: "Next",
                    description: "Next button title in a StandardStep of a Wizard component",
                  })}
              </Button>
            </Tooltip>
          </div>
        </div>
      ) : (
        footer
      )}
    </div>
  );
};

export default StandardStep;
